import React, { useState } from 'react';
import { 
  ArrowLeft, User, MapPin, Phone, Mail, MessageSquare, Star, Clock, 
  Calendar, Euro, Settings, Eye, Edit, Plus, Trash2, Upload, Camera,
  CheckCircle, XCircle, AlertTriangle, Users, Timer, Award, CreditCard,
  FileText, Percent, Save, X, ChevronDown, ChevronUp, Home, Car, Plane,
  Shield, Baby, Utensils, Music, Heart, Briefcase, Gift, Zap, Globe
} from 'lucide-react';

const LeadDetail = ({ lead, onBack, onSave }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [editMode, setEditMode] = useState({
    general: false,
    client: false,
    travel: false,
    preferences: false,
    financial: false,
    communications: false,
    notes: false
  });

  const [editingLead, setEditingLead] = useState({
    // Informations générales
    id: lead.id || 'ENQ001',
    status: lead.status || 'enquiry',
    priority: lead.priority || 'normal',
    urgency: lead.urgency || 'normal',
    source: lead.source || 'Website',
    referral: lead.referral || '',
    assignedTo: lead.assignedTo || 'Marie Laurent',
    createdAt: lead.createdAt || new Date().toISOString(),
    updatedAt: lead.updatedAt || new Date().toISOString(),

    // Informations client
    client: {
      title: lead.client?.title || 'Mr.',
      firstName: lead.client?.firstName || 'Marie',
      lastName: lead.client?.lastName || 'Dubois',
      email: lead.client?.email || 'marie.dubois@email.com',
      phone: lead.client?.phone || '+33 6 12 34 56 78',
      whatsapp: lead.client?.whatsapp || '+33 6 12 34 56 78',
      nationality: lead.client?.nationality || 'French',
      dateOfBirth: lead.client?.dateOfBirth || '1985-03-15',
      passportNumber: lead.client?.passportNumber || 'FR123456789',
      address: {
        street: lead.client?.address?.street || '123 Avenue des Champs-Élysées',
        city: lead.client?.address?.city || 'Paris',
        postalCode: lead.client?.address?.postalCode || '75008',
        country: lead.client?.address?.country || 'France'
      },
      emergencyContact: {
        name: lead.client?.emergencyContact?.name || 'Pierre Dubois',
        relationship: lead.client?.emergencyContact?.relationship || 'Spouse',
        phone: lead.client?.emergencyContact?.phone || '+33 6 98 76 54 32'
      },
      loyaltyTier: lead.client?.loyaltyTier || 'Gold',
      previousStays: lead.client?.previousStays || 3,
      totalSpent: lead.client?.totalSpent || 450000,
      notes: lead.client?.notes || 'Client régulier, préfère les villas avec vue mer'
    },

    // Informations voyage
    travel: {
      villa: lead.villa || 'Villa Azure',
      destination: lead.destination || 'St-Barth',
      checkIn: lead.dates?.split(' - ')[0] || '2024-12-15',
      checkOut: lead.dates?.split(' - ')[1] || '2025-01-05',
      duration: lead.duration || '21 nuits',
      adults: lead.travel?.adults || 6,
      children: lead.travel?.children || 2,
      infants: lead.travel?.infants || 0,
      totalGuests: lead.travel?.totalGuests || 8,
      rooms: lead.travel?.rooms || 4,
      occasion: lead.travel?.occasion || 'Festive',
      flexibility: lead.travel?.flexibility || 'Fixed dates',
      budget: lead.amount || 125000,
      budgetCurrency: lead.travel?.budgetCurrency || 'EUR',
      paymentMethod: lead.travel?.paymentMethod || 'Bank Transfer',
      specialRequests: lead.travel?.specialRequests || 'Décoration festive pour le Nouvel An'
    },

    // Préférences & Services
    preferences: {
      villaType: ['Waterfront', 'Modern', 'Large Pool'],
      amenities: ['Pool', 'Gym', 'Spa', 'Beach Access', 'Staff'],
      dietaryRestrictions: ['Gluten-free', 'Vegetarian options'],
      activities: ['Water Sports', 'Spa Services', 'Private Dining'],
      transportation: {
        airport: 'Private Jet',
        local: 'Chauffeur Service',
        preferences: 'Luxury vehicles only'
      },
      staff: {
        chef: true,
        housekeeper: true,
        concierge: true,
        security: false,
        nanny: true
      },
      catering: {
        breakfast: 'Daily',
        lunch: 'On request',
        dinner: '3 times per week',
        specialMeals: 'New Year\'s Eve dinner'
      }
    },

    // Informations financières
    financial: {
      basePrice: lead.amount || 125000,
      services: [
        { name: 'Chef Service', amount: 15000, included: true },
        { name: 'Spa Services', amount: 8000, included: false },
        { name: 'Private Jet', amount: 45000, included: false },
        { name: 'Yacht Charter', amount: 25000, included: false }
      ],
      totalEstimate: 218000,
      deposit: 65400, // 30%
      finalPayment: 152600,
      paymentSchedule: [
        { date: '2024-11-15', amount: 65400, description: 'Deposit (30%)', status: 'pending' },
        { date: '2024-12-01', amount: 76300, description: 'Second payment (35%)', status: 'pending' },
        { date: '2024-12-10', amount: 76300, description: 'Final payment (35%)', status: 'pending' }
      ],
      currency: 'EUR',
      taxesIncluded: true,
      commission: 12000,
      commissionRate: 5.5
    },

    // Communications
    communications: [
      {
        id: 'comm-001',
        date: '2024-10-15 14:30',
        type: 'email',
        direction: 'inbound',
        subject: 'Inquiry for Villa Azure',
        content: 'Hello, I am interested in renting Villa Azure for the festive season...',
        attachments: [],
        status: 'read'
      },
      {
        id: 'comm-002',
        date: '2024-10-15 16:45',
        type: 'phone',
        direction: 'outbound',
        subject: 'Initial consultation call',
        content: 'Discussed requirements, confirmed dates and preferences',
        duration: '25 minutes',
        status: 'completed'
      },
      {
        id: 'comm-003',
        date: '2024-10-16 10:15',
        type: 'email',
        direction: 'outbound',
        subject: 'Villa Azure - Detailed Proposal',
        content: 'Please find attached the detailed proposal for your stay...',
        attachments: ['Villa_Azure_Proposal.pdf', 'Services_Menu.pdf'],
        status: 'sent'
      }
    ],

    // Notes et historique
    notes: [
      {
        id: 'note-001',
        date: '2024-10-15 14:30',
        author: 'Marie Laurent',
        type: 'general',
        content: 'Client très intéressé, budget confirmé. Famille avec enfants, priorité sécurité.',
        tags: ['family', 'security', 'confirmed-budget']
      },
      {
        id: 'note-002',
        date: '2024-10-15 16:45',
        author: 'Marie Laurent',
        type: 'call',
        content: 'Appel téléphonique: préfère villa avec plage privée, demande chef spécialisé cuisine française.',
        tags: ['private-beach', 'french-cuisine', 'chef']
      },
      {
        id: 'note-003',
        date: '2024-10-16 09:00',
        author: 'Pierre Moreau',
        type: 'internal',
        content: 'Villa Azure disponible pour les dates, contacter chef Jean-Luc pour le menu festif.',
        tags: ['availability-confirmed', 'chef-contact']
      }
    ],

    // Documents
    documents: [
      { id: 'doc-001', name: 'Passport Copy.pdf', type: 'identity', uploadDate: '2024-10-15', size: '2.3 MB' },
      { id: 'doc-002', name: 'Villa Preferences.docx', type: 'requirements', uploadDate: '2024-10-16', size: '156 KB' },
      { id: 'doc-003', name: 'Special Requests.pdf', type: 'requirements', uploadDate: '2024-10-16', size: '89 KB' }
    ]
  });

  const handleToggleEdit = (section) => {
    setEditMode({ ...editMode, [section]: !editMode[section] });
  };

  const handleSave = (section) => {
    onSave(editingLead);
    setEditMode({ ...editMode, [section]: false });
  };

  const handleCancel = (section) => {
    setEditingLead({ ...editingLead });
    setEditMode({ ...editMode, [section]: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'enquiry': return 'bg-blue-100 text-blue-800';
      case 'qualification': return 'bg-indigo-100 text-indigo-800';
      case 'proposition': return 'bg-yellow-100 text-yellow-800';
      case 'negotiation': return 'bg-orange-100 text-orange-800';
      case 'contrat': return 'bg-purple-100 text-purple-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'VIP': return 'bg-yellow-100 text-yellow-800';
      case 'VVIP': return 'bg-purple-100 text-purple-800';
      case 'Celebrity': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="lead-detail min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={20} />
              <span>Retour au workflow</span>
            </button>
            <div className="border-l border-gray-300 pl-4">
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-gray-800">
                  {editingLead.travel.villa} - {editingLead.travel.destination}
                </h1>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(editingLead.status)}`}>
                  {editingLead.status}
                </span>
                {editingLead.priority && (
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getPriorityColor(editingLead.priority)}`}>
                    {editingLead.priority}
                  </span>
                )}
              </div>
              <p className="text-gray-600 mt-1">{editingLead.client.firstName} {editingLead.client.lastName} • {editingLead.travel.checkIn} - {editingLead.travel.checkOut}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              <MessageSquare size={18} />
              <span>WhatsApp</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Phone size={18} />
              <span>Appeler</span>
            </button>
          </div>
        </div>

        {/* KPIs de résumé */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Euro className="text-blue-600" size={20} />
              <span className="text-xs text-blue-600">Budget</span>
            </div>
            <div className="text-xl font-bold text-gray-800">€{editingLead.financial.totalEstimate.toLocaleString()}</div>
            <div className="text-sm text-gray-600">{editingLead.travel.duration}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-green-600" size={20} />
              <span className="text-xs text-green-600">Invités</span>
            </div>
            <div className="text-xl font-bold text-green-600">{editingLead.travel.totalGuests}</div>
            <div className="text-sm text-gray-600">{editingLead.travel.adults}A + {editingLead.travel.children}E</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="text-yellow-600" size={20} />
              <span className="text-xs text-yellow-600">Flexibilité</span>
            </div>
            <div className="text-lg font-bold text-yellow-600">{editingLead.travel.flexibility}</div>
            <div className="text-sm text-gray-600">{editingLead.travel.occasion}</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Star className="text-purple-600" size={20} />
              <span className="text-xs text-purple-600">Tier</span>
            </div>
            <div className="text-lg font-bold text-purple-600">{editingLead.client.loyaltyTier}</div>
            <div className="text-sm text-gray-600">{editingLead.client.previousStays} séjours</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="text-red-600" size={20} />
              <span className="text-xs text-red-600">Urgence</span>
            </div>
            <div className="text-lg font-bold text-red-600 capitalize">{editingLead.urgency}</div>
            <div className="text-sm text-gray-600">Source: {editingLead.source}</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Onglets */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'general', label: 'Informations générales', icon: Settings },
                { id: 'client', label: 'Client', icon: User },
                { id: 'travel', label: 'Voyage', icon: Plane },
                { id: 'preferences', label: 'Préférences & Services', icon: Heart },
                { id: 'financial', label: 'Financier', icon: CreditCard },
                { id: 'communications', label: 'Communications', icon: MessageSquare },
                { id: 'notes', label: 'Notes & Historique', icon: FileText }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon size={18} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Onglet Informations générales */}
            {activeTab === 'general' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Informations générales du lead</h3>
                    <p className="text-gray-600 mt-1">Vue d'ensemble et gestion du statut du lead</p>
                  </div>
                  {!editMode.general && (
                    <button
                      onClick={() => handleToggleEdit('general')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 shadow-lg transition-all duration-200"
                    >
                      <Edit size={18} />
                      <span>Modifier</span>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {/* Statut et Priorité */}
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <Settings className="text-white" size={20} />
                        </div>
                        Statut et Priorité
                      </h4>
                    </div>
                    <div className="p-6 space-y-6">
                      <div className="bg-blue-50 rounded-xl p-4">
                        <label className="block text-sm font-semibold text-blue-700 mb-2">ID Lead</label>
                        <div className="font-mono text-xl font-bold text-blue-800 bg-white px-4 py-2 rounded-lg border border-blue-200">
                          {editingLead.id}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Statut du lead</label>
                        {editMode.general ? (
                          <select
                            value={editingLead.status}
                            onChange={(e) => setEditingLead({...editingLead, status: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-medium"
                          >
                            <option value="enquiry">🔍 Enquête</option>
                            <option value="qualification">🎯 Qualification</option>
                            <option value="proposition">📋 Proposition</option>
                            <option value="negotiation">💬 Négociation</option>
                            <option value="contrat">📝 Contrat</option>
                            <option value="confirmed">✅ Confirmé</option>
                            <option value="cancelled">❌ Annulé</option>
                          </select>
                        ) : (
                          <div className="flex items-center space-x-3">
                            <span className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-full ${getStatusColor(editingLead.status)}`}>
                              {editingLead.status === 'enquiry' && '🔍'}
                              {editingLead.status === 'qualification' && '🎯'}
                              {editingLead.status === 'proposition' && '📋'}
                              {editingLead.status === 'negotiation' && '💬'}
                              {editingLead.status === 'contrat' && '📝'}
                              {editingLead.status === 'confirmed' && '✅'}
                              {editingLead.status === 'cancelled' && '❌'}
                              <span className="ml-2 capitalize">{editingLead.status}</span>
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-3">Priorité</label>
                          {editMode.general ? (
                            <select
                              value={editingLead.priority || ''}
                              onChange={(e) => setEditingLead({...editingLead, priority: e.target.value || null})}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Normale</option>
                              <option value="VIP">⭐ VIP</option>
                              <option value="VVIP">🌟 VVIP</option>
                              <option value="Celebrity">👑 Celebrity</option>
                            </select>
                          ) : (
                            <span className={`inline-flex items-center px-3 py-2 text-sm font-semibold rounded-full ${getPriorityColor(editingLead.priority)}`}>
                              {editingLead.priority === 'VIP' && '⭐'}
                              {editingLead.priority === 'VVIP' && '🌟'}
                              {editingLead.priority === 'Celebrity' && '👑'}
                              <span className="ml-1">{editingLead.priority || 'Normale'}</span>
                            </span>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-3">Urgence</label>
                          {editMode.general ? (
                            <select
                              value={editingLead.urgency}
                              onChange={(e) => setEditingLead({...editingLead, urgency: e.target.value})}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="low">⬇️ Faible</option>
                              <option value="normal">➡️ Normal</option>
                              <option value="urgent">⚡ Urgent</option>
                              <option value="critical">🔥 Critique</option>
                            </select>
                          ) : (
                            <div className="flex items-center space-x-2">
                              {editingLead.urgency === 'low' && <span className="text-lg">⬇️</span>}
                              {editingLead.urgency === 'normal' && <span className="text-lg">➡️</span>}
                              {editingLead.urgency === 'urgent' && <span className="text-lg">⚡</span>}
                              {editingLead.urgency === 'critical' && <span className="text-lg">🔥</span>}
                              <span className="font-semibold text-gray-800 capitalize">{editingLead.urgency}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Origine et Assignation */}
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <Globe className="text-white" size={20} />
                        </div>
                        Origine et Assignation
                      </h4>
                    </div>
                    <div className="p-6 space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Source</label>
                        {editMode.general ? (
                          <select
                            value={editingLead.source}
                            onChange={(e) => setEditingLead({...editingLead, source: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="Website">🌐 Site Web</option>
                            <option value="Referral">🤝 Recommandation</option>
                            <option value="Agent">👨‍💼 Agent</option>
                            <option value="Social Media">📱 Réseaux Sociaux</option>
                            <option value="Direct">📞 Contact Direct</option>
                            <option value="Event">🎉 Événement</option>
                          </select>
                        ) : (
                          <div className="bg-green-50 px-4 py-3 rounded-xl border border-green-200">
                            <span className="font-semibold text-green-800">
                              {editingLead.source === 'Website' && '🌐 '}
                              {editingLead.source === 'Referral' && '🤝 '}
                              {editingLead.source === 'Agent' && '👨‍💼 '}
                              {editingLead.source === 'Social Media' && '📱 '}
                              {editingLead.source === 'Direct' && '📞 '}
                              {editingLead.source === 'Event' && '🎉 '}
                              {editingLead.source}
                            </span>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Recommandation</label>
                        {editMode.general ? (
                          <input
                            type="text"
                            value={editingLead.referral}
                            onChange={(e) => setEditingLead({...editingLead, referral: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Nom du référent (si applicable)"
                          />
                        ) : (
                          <div className="bg-gray-50 px-4 py-3 rounded-xl border border-gray-200">
                            <span className="font-medium text-gray-800">{editingLead.referral || 'Aucune'}</span>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Assigné à</label>
                        {editMode.general ? (
                          <select
                            value={editingLead.assignedTo}
                            onChange={(e) => setEditingLead({...editingLead, assignedTo: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="Marie Laurent">👩‍💼 Marie Laurent</option>
                            <option value="Pierre Moreau">👨‍💼 Pierre Moreau</option>
                            <option value="Sophie Martin">👩‍💼 Sophie Martin</option>
                            <option value="Jean Dubois">👨‍💼 Jean Dubois</option>
                          </select>
                        ) : (
                          <div className="flex items-center space-x-3 bg-blue-50 px-4 py-3 rounded-xl border border-blue-200">
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                              <User className="text-white" size={16} />
                            </div>
                            <span className="font-semibold text-blue-800">{editingLead.assignedTo}</span>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Créé le</label>
                        <div className="bg-gray-50 px-4 py-3 rounded-xl border border-gray-200">
                          <div className="flex items-center space-x-2">
                            <Calendar className="text-gray-600" size={16} />
                            <span className="font-medium text-gray-800">
                              {new Date(editingLead.createdAt).toLocaleDateString('fr-FR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {editMode.general && (
                  <div className="flex justify-end space-x-4 bg-gray-50 px-6 py-4 rounded-xl border border-gray-200">
                    <button
                      onClick={() => handleSave('general')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg transition-all duration-200"
                    >
                      <Save size={18} />
                      <span>Sauvegarder</span>
                    </button>
                    <button
                      onClick={() => handleCancel('general')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 shadow-lg transition-all duration-200"
                    >
                      <X size={18} />
                      <span>Annuler</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Onglet Client */}
            {activeTab === 'client' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Profil client</h3>
                    <p className="text-gray-600 mt-1">Informations personnelles et historique de fidélité</p>
                  </div>
                  {!editMode.client && (
                    <button
                      onClick={() => handleToggleEdit('client')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 shadow-lg transition-all duration-200"
                    >
                      <Edit size={18} />
                      <span>Modifier</span>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                  {/* Informations personnelles */}
                  <div className="xl:col-span-2 bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <User className="text-white" size={20} />
                        </div>
                        Informations personnelles
                      </h4>
                    </div>
                    <div className="p-6 space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Titre</label>
                          {editMode.client ? (
                            <select
                              value={editingLead.client.title}
                              onChange={(e) => setEditingLead({
                                ...editingLead,
                                client: { ...editingLead.client, title: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            >
                              <option value="Mr.">Mr.</option>
                              <option value="Mrs.">Mrs.</option>
                              <option value="Ms.">Ms.</option>
                              <option value="Dr.">Dr.</option>
                              <option value="Prof.">Prof.</option>
                            </select>
                          ) : (
                            <div className="bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                              <span className="font-medium text-purple-800">{editingLead.client.title}</span>
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Prénom</label>
                          {editMode.client ? (
                            <input
                              type="text"
                              value={editingLead.client.firstName}
                              onChange={(e) => setEditingLead({
                                ...editingLead,
                                client: { ...editingLead.client, firstName: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                          ) : (
                            <div className="bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                              <span className="font-semibold text-gray-800">{editingLead.client.firstName}</span>
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Nom</label>
                          {editMode.client ? (
                            <input
                              type="text"
                              value={editingLead.client.lastName}
                              onChange={(e) => setEditingLead({
                                ...editingLead,
                                client: { ...editingLead.client, lastName: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                          ) : (
                            <div className="bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                              <span className="font-semibold text-gray-800">{editingLead.client.lastName}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                          {editMode.client ? (
                            <input
                              type="email"
                              value={editingLead.client.email}
                              onChange={(e) => setEditingLead({
                                ...editingLead,
                                client: { ...editingLead.client, email: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                          ) : (
                            <div className="flex items-center space-x-3 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                              <Mail className="text-blue-600" size={16} />
                              <span className="font-medium text-blue-800">{editingLead.client.email}</span>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Téléphone</label>
                          {editMode.client ? (
                            <input
                              type="tel"
                              value={editingLead.client.phone}
                              onChange={(e) => setEditingLead({
                                ...editingLead,
                                client: { ...editingLead.client, phone: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                          ) : (
                            <div className="flex items-center space-x-3 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
                              <Phone className="text-green-600" size={16} />
                              <span className="font-medium text-green-800">{editingLead.client.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">WhatsApp</label>
                          {editMode.client ? (
                            <input
                              type="tel"
                              value={editingLead.client.whatsapp}
                              onChange={(e) => setEditingLead({
                                ...editingLead,
                                client: { ...editingLead.client, whatsapp: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                          ) : (
                            <div className="flex items-center space-x-3 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
                              <MessageSquare className="text-green-600" size={16} />
                              <span className="font-medium text-green-800">{editingLead.client.whatsapp}</span>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Nationalité</label>
                          {editMode.client ? (
                            <input
                              type="text"
                              value={editingLead.client.nationality}
                              onChange={(e) => setEditingLead({
                                ...editingLead,
                                client: { ...editingLead.client, nationality: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                          ) : (
                            <div className="flex items-center space-x-3 bg-indigo-50 px-3 py-2 rounded-lg border border-indigo-200">
                              <Globe className="text-indigo-600" size={16} />
                              <span className="font-medium text-indigo-800">{editingLead.client.nationality}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Date de naissance</label>
                        {editMode.client ? (
                          <input
                            type="date"
                            value={editingLead.client.dateOfBirth}
                            onChange={(e) => setEditingLead({
                              ...editingLead,
                              client: { ...editingLead.client, dateOfBirth: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        ) : (
                          <div className="flex items-center space-x-3 bg-yellow-50 px-3 py-2 rounded-lg border border-yellow-200">
                            <Calendar className="text-yellow-600" size={16} />
                            <span className="font-medium text-yellow-800">
                              {new Date(editingLead.client.dateOfBirth).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Statut client */}
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <Award className="text-white" size={20} />
                        </div>
                        Statut VIP
                      </h4>
                    </div>
                    <div className="p-6 space-y-6">
                      <div className="text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Award className="text-white" size={32} />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Tier de fidélité</label>
                          {editMode.client ? (
                            <select
                              value={editingLead.client.loyaltyTier}
                              onChange={(e) => setEditingLead({
                                ...editingLead,
                                client: { ...editingLead.client, loyaltyTier: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            >
                              <option value="Bronze">🥉 Bronze</option>
                              <option value="Silver">🥈 Silver</option>
                              <option value="Gold">🥇 Gold</option>
                              <option value="Platinum">💎 Platinum</option>
                              <option value="Diamond">💠 Diamond</option>
                            </select>
                          ) : (
                            <div className="text-2xl font-bold text-yellow-600 mb-2">
                              {editingLead.client.loyaltyTier === 'Bronze' && '🥉'}
                              {editingLead.client.loyaltyTier === 'Silver' && '🥈'}
                              {editingLead.client.loyaltyTier === 'Gold' && '🥇'}
                              {editingLead.client.loyaltyTier === 'Platinum' && '💎'}
                              {editingLead.client.loyaltyTier === 'Diamond' && '💠'}
                              <span className="ml-2">{editingLead.client.loyaltyTier}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-blue-600 mb-1">{editingLead.client.previousStays}</div>
                          <div className="text-sm font-medium text-blue-700">Séjours précédents</div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600 mb-1">€{editingLead.client.totalSpent.toLocaleString()}</div>
                          <div className="text-sm font-medium text-green-700">Total dépensé</div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Notes client</label>
                        {editMode.client ? (
                          <textarea
                            value={editingLead.client.notes}
                            onChange={(e) => setEditingLead({
                              ...editingLead,
                              client: { ...editingLead.client, notes: e.target.value }
                            })}
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        ) : (
                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-800">{editingLead.client.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Adresse */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
                    <h4 className="text-lg font-semibold text-white flex items-center">
                      <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                        <Home className="text-white" size={20} />
                      </div>
                      Adresse de résidence
                    </h4>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Rue</label>
                        {editMode.client ? (
                          <input
                            type="text"
                            value={editingLead.client.address.street}
                            onChange={(e) => setEditingLead({
                              ...editingLead,
                              client: {
                                ...editingLead.client,
                                address: { ...editingLead.client.address, street: e.target.value }
                              }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        ) : (
                          <div className="bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                            <span className="font-medium text-gray-800">{editingLead.client.address.street}</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Ville</label>
                        {editMode.client ? (
                          <input
                            type="text"
                            value={editingLead.client.address.city}
                            onChange={(e) => setEditingLead({
                              ...editingLead,
                              client: {
                                ...editingLead.client,
                                address: { ...editingLead.client.address, city: e.target.value }
                              }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        ) : (
                          <div className="bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                            <span className="font-medium text-gray-800">{editingLead.client.address.city}</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Code postal</label>
                        {editMode.client ? (
                          <input
                            type="text"
                            value={editingLead.client.address.postalCode}
                            onChange={(e) => setEditingLead({
                              ...editingLead,
                              client: {
                                ...editingLead.client,
                                address: { ...editingLead.client.address, postalCode: e.target.value }
                              }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        ) : (
                          <div className="bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                            <span className="font-medium text-gray-800">{editingLead.client.address.postalCode}</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Pays</label>
                        {editMode.client ? (
                          <input
                            type="text"
                            value={editingLead.client.address.country}
                            onChange={(e) => setEditingLead({
                              ...editingLead,
                              client: {
                                ...editingLead.client,
                                address: { ...editingLead.client.address, country: e.target.value }
                              }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        ) : (
                          <div className="flex items-center space-x-3 bg-indigo-50 px-3 py-2 rounded-lg border border-indigo-200">
                            <MapPin className="text-indigo-600" size={16} />
                            <span className="font-medium text-indigo-800">{editingLead.client.address.country}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {editMode.client && (
                  <div className="flex justify-end space-x-4 bg-gray-50 px-6 py-4 rounded-xl border border-gray-200">
                    <button
                      onClick={() => handleSave('client')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg transition-all duration-200"
                    >
                      <Save size={18} />
                      <span>Sauvegarder</span>
                    </button>
                    <button
                      onClick={() => handleCancel('client')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 shadow-lg transition-all duration-200"
                    >
                      <X size={18} />
                      <span>Annuler</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Les autres onglets seraient implémentés de la même manière */}
            {/* Onglet Voyage */}
            {activeTab === 'travel' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Détails du voyage</h3>
                    <p className="text-gray-600 mt-1">Informations sur le séjour et les préférences de voyage</p>
                  </div>
                  {!editMode.travel && (
                    <button
                      onClick={() => handleToggleEdit('travel')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-xl hover:from-orange-700 hover:to-orange-800 shadow-lg transition-all duration-200"
                    >
                      <Edit size={18} />
                      <span>Modifier</span>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {/* Villa et Destination */}
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <Home className="text-white" size={20} />
                        </div>
                        Villa et Destination
                      </h4>
                    </div>
                    <div className="p-6 space-y-6">
                      <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-orange-600 mb-2">{editingLead.travel.villa}</div>
                          <div className="flex items-center justify-center space-x-2 text-orange-700">
                            <MapPin size={16} />
                            <span className="font-medium">{editingLead.travel.destination}</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Check-in</label>
                          {editMode.travel ? (
                            <input
                              type="date"
                              value={editingLead.travel.checkIn}
                              onChange={(e) => setEditingLead({
                                ...editingLead,
                                travel: { ...editingLead.travel, checkIn: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                          ) : (
                            <div className="flex items-center space-x-2 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
                              <Calendar className="text-green-600" size={16} />
                              <span className="font-medium text-green-800">
                                {new Date(editingLead.travel.checkIn).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Check-out</label>
                          {editMode.travel ? (
                            <input
                              type="date"
                              value={editingLead.travel.checkOut}
                              onChange={(e) => setEditingLead({
                                ...editingLead,
                                travel: { ...editingLead.travel, checkOut: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                          ) : (
                            <div className="flex items-center space-x-2 bg-red-50 px-3 py-2 rounded-lg border border-red-200">
                              <Calendar className="text-red-600" size={16} />
                              <span className="font-medium text-red-800">
                                {new Date(editingLead.travel.checkOut).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600 mb-1">{editingLead.travel.duration}</div>
                          <div className="text-sm font-medium text-blue-700">Durée du séjour</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Occasion</label>
                          {editMode.travel ? (
                            <select
                              value={editingLead.travel.occasion}
                              onChange={(e) => setEditingLead({
                                ...editingLead,
                                travel: { ...editingLead.travel, occasion: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            >
                              <option value="Festive">🎉 Festive</option>
                              <option value="Anniversary">💕 Anniversaire</option>
                              <option value="Wedding">💒 Mariage</option>
                              <option value="Birthday">🎂 Anniversaire</option>
                              <option value="Business">💼 Affaires</option>
                              <option value="Vacation">🏖️ Vacances</option>
                              <option value="Honeymoon">🌙 Lune de miel</option>
                            </select>
                          ) : (
                            <div className="bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                              <span className="font-medium text-purple-800">
                                {editingLead.travel.occasion === 'Festive' && '🎉 '}
                                {editingLead.travel.occasion === 'Anniversary' && '💕 '}
                                {editingLead.travel.occasion === 'Wedding' && '💒 '}
                                {editingLead.travel.occasion === 'Birthday' && '🎂 '}
                                {editingLead.travel.occasion === 'Business' && '💼 '}
                                {editingLead.travel.occasion === 'Vacation' && '🏖️ '}
                                {editingLead.travel.occasion === 'Honeymoon' && '🌙 '}
                                {editingLead.travel.occasion}
                              </span>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Flexibilité</label>
                          {editMode.travel ? (
                            <select
                              value={editingLead.travel.flexibility}
                              onChange={(e) => setEditingLead({
                                ...editingLead,
                                travel: { ...editingLead.travel, flexibility: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            >
                              <option value="Fixed dates">Dates fixes</option>
                              <option value="Flexible (+/- 3 days)">Flexible (+/- 3 jours)</option>
                              <option value="Flexible (+/- 1 week)">Flexible (+/- 1 semaine)</option>
                              <option value="Very flexible">Très flexible</option>
                            </select>
                          ) : (
                            <div className="bg-yellow-50 px-3 py-2 rounded-lg border border-yellow-200">
                              <span className="font-medium text-yellow-800">{editingLead.travel.flexibility}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Invités */}
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <Users className="text-white" size={20} />
                        </div>
                        Composition du groupe
                      </h4>
                    </div>
                    <div className="p-6 space-y-6">
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-blue-600 mb-2">{editingLead.travel.totalGuests}</div>
                          <div className="text-sm font-medium text-blue-700">Total invités</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                          <div className="text-2xl font-bold text-green-600 mb-1">{editingLead.travel.adults}</div>
                          <div className="text-xs font-medium text-green-700">Adultes</div>
                        </div>
                        <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                          <div className="text-2xl font-bold text-yellow-600 mb-1">{editingLead.travel.children}</div>
                          <div className="text-xs font-medium text-yellow-700">Enfants</div>
                        </div>
                        <div className="bg-pink-50 p-4 rounded-xl border border-pink-200">
                          <div className="text-2xl font-bold text-pink-600 mb-1">{editingLead.travel.infants}</div>
                          <div className="text-xs font-medium text-pink-700">Bébés</div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Chambres requises</label>
                        <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-200 text-center">
                          <div className="text-2xl font-bold text-indigo-600">{editingLead.travel.rooms}</div>
                          <div className="text-sm font-medium text-indigo-700">Chambres</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Budget et Paiement */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
                    <h4 className="text-lg font-semibold text-white flex items-center">
                      <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                        <Euro className="text-white" size={20} />
                      </div>
                      Budget et Paiement
                    </h4>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-green-600 mb-2">€{editingLead.travel.budget.toLocaleString()}</div>
                          <div className="text-sm font-medium text-green-700">Budget confirmé</div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Devise</label>
                        {editMode.travel ? (
                          <select
                            value={editingLead.travel.budgetCurrency}
                            onChange={(e) => setEditingLead({
                              ...editingLead,
                              travel: { ...editingLead.travel, budgetCurrency: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          >
                            <option value="EUR">EUR (€)</option>
                            <option value="USD">USD ($)</option>
                            <option value="GBP">GBP (£)</option>
                            <option value="CHF">CHF</option>
                          </select>
                        ) : (
                          <div className="bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                            <span className="font-medium text-gray-800">{editingLead.travel.budgetCurrency}</span>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Mode de paiement</label>
                        {editMode.travel ? (
                          <select
                            value={editingLead.travel.paymentMethod}
                            onChange={(e) => setEditingLead({
                              ...editingLead,
                              travel: { ...editingLead.travel, paymentMethod: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          >
                            <option value="Bank Transfer">Virement bancaire</option>
                            <option value="Credit Card">Carte de crédit</option>
                            <option value="Cash">Espèces</option>
                            <option value="Check">Chèque</option>
                          </select>
                        ) : (
                          <div className="flex items-center space-x-2 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                            <CreditCard className="text-blue-600" size={16} />
                            <span className="font-medium text-blue-800">{editingLead.travel.paymentMethod}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Demandes spéciales */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                    <h4 className="text-lg font-semibold text-white flex items-center">
                      <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                        <Star className="text-white" size={20} />
                      </div>
                      Demandes spéciales
                    </h4>
                  </div>
                  <div className="p-6">
                    {editMode.travel ? (
                      <textarea
                        value={editingLead.travel.specialRequests}
                        onChange={(e) => setEditingLead({
                          ...editingLead,
                          travel: { ...editingLead.travel, specialRequests: e.target.value }
                        })}
                        rows="4"
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Décrivez les demandes spéciales du client..."
                      />
                    ) : (
                      <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                        <p className="text-purple-800 font-medium">{editingLead.travel.specialRequests}</p>
                      </div>
                    )}
                  </div>
                </div>

                {editMode.travel && (
                  <div className="flex justify-end space-x-4 bg-gray-50 px-6 py-4 rounded-xl border border-gray-200">
                    <button
                      onClick={() => handleSave('travel')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg transition-all duration-200"
                    >
                      <Save size={18} />
                      <span>Sauvegarder</span>
                    </button>
                    <button
                      onClick={() => handleCancel('travel')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 shadow-lg transition-all duration-200"
                    >
                      <X size={18} />
                      <span>Annuler</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Préférences & Services</h3>
                    <p className="text-gray-600 mt-1">Configuration des préférences client et services demandés</p>
                  </div>
                  {!editMode.preferences && (
                    <button
                      onClick={() => handleToggleEdit('preferences')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 shadow-lg transition-all duration-200"
                    >
                      <Edit size={18} />
                      <span>Modifier</span>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {/* Type de Villa & Équipements */}
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <Home className="text-white" size={20} />
                        </div>
                        Type de Villa & Équipements
                      </h4>
                    </div>
                    <div className="p-6 space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Type de villa souhaité</label>
                        <div className="flex flex-wrap gap-2">
                          {editingLead.preferences.villaType.map((type, index) => (
                            <span key={index} className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                              <Home size={14} className="mr-2" />
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Équipements requis</label>
                        <div className="grid grid-cols-2 gap-3">
                          {editingLead.preferences.amenities.map((amenity, index) => (
                            <div key={index} className="flex items-center space-x-2 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
                              {amenity === 'Pool' && <div className="w-3 h-3 bg-blue-500 rounded-full"></div>}
                              {amenity === 'Gym' && <div className="w-3 h-3 bg-red-500 rounded-full"></div>}
                              {amenity === 'Spa' && <div className="w-3 h-3 bg-purple-500 rounded-full"></div>}
                              {amenity === 'Beach Access' && <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>}
                              {amenity === 'Staff' && <div className="w-3 h-3 bg-gray-500 rounded-full"></div>}
                              <span className="text-sm font-medium text-green-800">{amenity}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Restrictions alimentaires</label>
                        <div className="space-y-2">
                          {editingLead.preferences.dietaryRestrictions.map((restriction, index) => (
                            <div key={index} className="flex items-center space-x-2 bg-orange-50 px-3 py-2 rounded-lg border border-orange-200">
                              <Utensils className="text-orange-600" size={16} />
                              <span className="text-sm font-medium text-orange-800">{restriction}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Services & Personnel */}
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <Users className="text-white" size={20} />
                        </div>
                        Services & Personnel
                      </h4>
                    </div>
                    <div className="p-6 space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Personnel demandé</label>
                        <div className="grid grid-cols-2 gap-3">
                          {Object.entries(editingLead.preferences.staff).map(([key, value]) => (
                            <div key={key} className={`flex items-center justify-between p-3 rounded-lg border ${value ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                              <div className="flex items-center space-x-2">
                                {key === 'chef' && <Utensils className={`text-${value ? 'green' : 'gray'}-600`} size={16} />}
                                {key === 'housekeeper' && <Home className={`text-${value ? 'green' : 'gray'}-600`} size={16} />}
                                {key === 'concierge' && <Award className={`text-${value ? 'green' : 'gray'}-600`} size={16} />}
                                {key === 'security' && <Shield className={`text-${value ? 'green' : 'gray'}-600`} size={16} />}
                                {key === 'nanny' && <Baby className={`text-${value ? 'green' : 'gray'}-600`} size={16} />}
                                <span className={`text-sm font-medium capitalize text-${value ? 'green' : 'gray'}-800`}>
                                  {key === 'housekeeper' ? 'Femme de ménage' : 
                                   key === 'nanny' ? 'Nounou' :
                                   key === 'security' ? 'Sécurité' :
                                   key === 'concierge' ? 'Concierge' : 'Chef'}
                                </span>
                              </div>
                              <div className={`w-3 h-3 rounded-full ${value ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Restauration</label>
                        <div className="space-y-3">
                          {Object.entries(editingLead.preferences.catering).map(([meal, frequency]) => (
                            <div key={meal} className="flex items-center justify-between bg-purple-50 px-4 py-3 rounded-lg border border-purple-200">
                              <div className="flex items-center space-x-2">
                                <Utensils className="text-purple-600" size={16} />
                                <span className="text-sm font-medium text-purple-800 capitalize">
                                  {meal === 'breakfast' ? 'Petit-déjeuner' :
                                   meal === 'lunch' ? 'Déjeuner' :
                                   meal === 'dinner' ? 'Dîner' : 'Repas spéciaux'}
                                </span>
                              </div>
                              <span className="text-sm font-semibold text-purple-700 bg-purple-100 px-2 py-1 rounded-full">
                                {frequency}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Activités souhaitées</label>
                        <div className="flex flex-wrap gap-2">
                          {editingLead.preferences.activities.map((activity, index) => (
                            <span key={index} className="inline-flex items-center px-3 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                              {activity === 'Water Sports' && <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>}
                              {activity === 'Spa Services' && <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>}
                              {activity === 'Private Dining' && <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>}
                              {activity}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transport Section */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
                    <h4 className="text-lg font-semibold text-white flex items-center">
                      <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                        <Car className="text-white" size={20} />
                      </div>
                      Services de Transport
                    </h4>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Plane className="text-blue-600" size={24} />
                        </div>
                        <h5 className="font-semibold text-gray-800 mb-2">Transport aéroport</h5>
                        <p className="text-lg font-bold text-blue-600">{editingLead.preferences.transportation.airport}</p>
                      </div>

                      <div className="text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Car className="text-green-600" size={24} />
                        </div>
                        <h5 className="font-semibold text-gray-800 mb-2">Transport local</h5>
                        <p className="text-lg font-bold text-green-600">{editingLead.preferences.transportation.local}</p>
                      </div>

                      <div className="text-center">
                        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Star className="text-purple-600" size={24} />
                        </div>
                        <h5 className="font-semibold text-gray-800 mb-2">Préférences</h5>
                        <p className="text-sm text-purple-600 font-medium">{editingLead.preferences.transportation.preferences}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {editMode.preferences && (
                  <div className="flex justify-end space-x-4 bg-gray-50 px-6 py-4 rounded-xl border border-gray-200">
                    <button
                      onClick={() => handleSave('preferences')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg transition-all duration-200"
                    >
                      <Save size={18} />
                      <span>Sauvegarder</span>
                    </button>
                    <button
                      onClick={() => handleCancel('preferences')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 shadow-lg transition-all duration-200"
                    >
                      <X size={18} />
                      <span>Annuler</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'financial' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Informations Financières</h3>
                    <p className="text-gray-600 mt-1">Tarification, commissions et échéancier de paiement</p>
                  </div>
                  {!editMode.financial && (
                    <button
                      onClick={() => handleToggleEdit('financial')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg transition-all duration-200"
                    >
                      <Edit size={18} />
                      <span>Modifier</span>
                    </button>
                  )}
                </div>

                {/* KPIs Financiers */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <Euro className="text-white opacity-80" size={24} />
                      <span className="text-xs text-blue-100 font-medium">Prix de base</span>
                    </div>
                    <div className="text-3xl font-bold mb-1">€{editingLead.financial.basePrice.toLocaleString()}</div>
                    <div className="text-blue-100 text-sm">Villa + Services inclus</div>
                  </div>

                  <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <CreditCard className="text-white opacity-80" size={24} />
                      <span className="text-xs text-green-100 font-medium">Total estimé</span>
                    </div>
                    <div className="text-3xl font-bold mb-1">€{editingLead.financial.totalEstimate.toLocaleString()}</div>
                    <div className="text-green-100 text-sm">Tous services inclus</div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <Percent className="text-white opacity-80" size={24} />
                      <span className="text-xs text-purple-100 font-medium">Commission</span>
                    </div>
                    <div className="text-3xl font-bold mb-1">€{editingLead.financial.commission.toLocaleString()}</div>
                    <div className="text-purple-100 text-sm">{editingLead.financial.commissionRate}% du total</div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <AlertTriangle className="text-white opacity-80" size={24} />
                      <span className="text-xs text-orange-100 font-medium">Acompte requis</span>
                    </div>
                    <div className="text-3xl font-bold mb-1">€{editingLead.financial.deposit.toLocaleString()}</div>
                    <div className="text-orange-100 text-sm">30% du total</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {/* Services Additionnels */}
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <Gift className="text-white" size={20} />
                        </div>
                        Services Additionnels
                      </h4>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        {editingLead.financial.services.map((service, index) => (
                          <div key={index} className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-200 ${service.included ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                            <div className="flex items-center space-x-3">
                              <div className={`w-4 h-4 rounded-full ${service.included ? 'bg-green-500' : 'bg-orange-500'}`}></div>
                              <div>
                                <h5 className={`font-semibold ${service.included ? 'text-green-800' : 'text-orange-800'}`}>
                                  {service.name}
                                </h5>
                                <p className={`text-xs ${service.included ? 'text-green-600' : 'text-orange-600'}`}>
                                  {service.included ? 'Inclus dans le tarif' : 'Service optionnel'}
                                </p>
                              </div>
                            </div>
                            <div className={`text-right font-bold ${service.included ? 'text-green-600' : 'text-orange-600'}`}>
                              €{service.amount.toLocaleString()}
                              {service.included && <div className="text-xs text-green-500">Inclus</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Échéancier de Paiement */}
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <Calendar className="text-white" size={20} />
                        </div>
                        Échéancier de Paiement
                      </h4>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        {editingLead.financial.paymentSchedule.map((payment, index) => (
                          <div key={index} className="relative">
                            <div className={`p-4 rounded-xl border transition-all duration-200 ${
                              payment.status === 'paid' ? 'bg-green-50 border-green-200' :
                              payment.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
                              'bg-gray-50 border-gray-200'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                    payment.status === 'paid' ? 'bg-green-500' :
                                    payment.status === 'pending' ? 'bg-yellow-500' :
                                    'bg-gray-400'
                                  }`}>
                                    {payment.status === 'paid' ? (
                                      <CheckCircle className="text-white" size={14} />
                                    ) : payment.status === 'pending' ? (
                                      <Clock className="text-white" size={14} />
                                    ) : (
                                      <XCircle className="text-white" size={14} />
                                    )}
                                  </div>
                                  <div>
                                    <h5 className={`font-semibold ${
                                      payment.status === 'paid' ? 'text-green-800' :
                                      payment.status === 'pending' ? 'text-yellow-800' :
                                      'text-gray-800'
                                    }`}>
                                      {payment.description}
                                    </h5>
                                    <p className={`text-sm ${
                                      payment.status === 'paid' ? 'text-green-600' :
                                      payment.status === 'pending' ? 'text-yellow-600' :
                                      'text-gray-600'
                                    }`}>
                                      {new Date(payment.date).toLocaleDateString('fr-FR', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                      })}
                                    </p>
                                  </div>
                                </div>
                                <div className={`text-right font-bold ${
                                  payment.status === 'paid' ? 'text-green-600' :
                                  payment.status === 'pending' ? 'text-yellow-600' :
                                  'text-gray-600'
                                }`}>
                                  €{payment.amount.toLocaleString()}
                                  <div className={`text-xs capitalize ${
                                    payment.status === 'paid' ? 'text-green-500' :
                                    payment.status === 'pending' ? 'text-yellow-500' :
                                    'text-gray-500'
                                  }`}>
                                    {payment.status === 'paid' ? 'Payé' :
                                     payment.status === 'pending' ? 'En attente' : 'Non payé'}
                                  </div>
                                </div>
                              </div>
                            </div>
                            {index < editingLead.financial.paymentSchedule.length - 1 && (
                              <div className="absolute left-6 top-16 w-0.5 h-4 bg-gray-300"></div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Informations Fiscales */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-gray-500 to-gray-600 px-6 py-4">
                    <h4 className="text-lg font-semibold text-white flex items-center">
                      <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                        <FileText className="text-white" size={20} />
                      </div>
                      Informations Fiscales
                    </h4>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Globe className="text-blue-600" size={24} />
                        </div>
                        <h5 className="font-semibold text-gray-800 mb-2">Devise</h5>
                        <p className="text-lg font-bold text-blue-600">{editingLead.financial.currency}</p>
                      </div>

                      <div className="text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckCircle className="text-green-600" size={24} />
                        </div>
                        <h5 className="font-semibold text-gray-800 mb-2">Taxes</h5>
                        <p className="text-lg font-bold text-green-600">
                          {editingLead.financial.taxesIncluded ? 'Incluses' : 'Non incluses'}
                        </p>
                      </div>

                      <div className="text-center">
                        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Percent className="text-purple-600" size={24} />
                        </div>
                        <h5 className="font-semibold text-gray-800 mb-2">Taux Commission</h5>
                        <p className="text-lg font-bold text-purple-600">{editingLead.financial.commissionRate}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                {editMode.financial && (
                  <div className="flex justify-end space-x-4 bg-gray-50 px-6 py-4 rounded-xl border border-gray-200">
                    <button
                      onClick={() => handleSave('financial')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg transition-all duration-200"
                    >
                      <Save size={18} />
                      <span>Sauvegarder</span>
                    </button>
                    <button
                      onClick={() => handleCancel('financial')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 shadow-lg transition-all duration-200"
                    >
                      <X size={18} />
                      <span>Annuler</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'communications' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Communications</h3>
                    <p className="text-gray-600 mt-1">Historique complet des interactions avec le client</p>
                  </div>
                  {!editMode.communications && (
                    <button
                      onClick={() => handleToggleEdit('communications')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 shadow-lg transition-all duration-200"
                    >
                      <Plus size={18} />
                      <span>Nouvelle communication</span>
                    </button>
                  )}
                </div>

                {/* Statistiques de communication */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <Mail className="text-white opacity-80" size={24} />
                      <span className="text-xs text-blue-100 font-medium">Emails</span>
                    </div>
                    <div className="text-3xl font-bold mb-1">
                      {editingLead.communications.filter(c => c.type === 'email').length}
                    </div>
                    <div className="text-blue-100 text-sm">Messages envoyés</div>
                  </div>

                  <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <Phone className="text-white opacity-80" size={24} />
                      <span className="text-xs text-green-100 font-medium">Appels</span>
                    </div>
                    <div className="text-3xl font-bold mb-1">
                      {editingLead.communications.filter(c => c.type === 'phone').length}
                    </div>
                    <div className="text-green-100 text-sm">Conversations</div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <MessageSquare className="text-white opacity-80" size={24} />
                      <span className="text-xs text-purple-100 font-medium">Total</span>
                    </div>
                    <div className="text-3xl font-bold mb-1">{editingLead.communications.length}</div>
                    <div className="text-purple-100 text-sm">Interactions</div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <Clock className="text-white opacity-80" size={24} />
                      <span className="text-xs text-orange-100 font-medium">Dernière</span>
                    </div>
                    <div className="text-lg font-bold mb-1">
                      {editingLead.communications.length > 0 ? 
                        new Date(editingLead.communications[editingLead.communications.length - 1].date).toLocaleDateString('fr-FR', {
                          month: 'short',
                          day: 'numeric'
                        }) : 'Aucune'
                      }
                    </div>
                    <div className="text-orange-100 text-sm">Communication</div>
                  </div>
                </div>

                {/* Timeline des communications */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
                    <h4 className="text-lg font-semibold text-white flex items-center">
                      <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                        <MessageSquare className="text-white" size={20} />
                      </div>
                      Timeline des Communications
                    </h4>
                  </div>
                  <div className="p-6">
                    <div className="space-y-6">
                      {editingLead.communications.map((comm, index) => (
                        <div key={comm.id} className="relative">
                          <div className="flex space-x-4">
                            {/* Timeline marker */}
                            <div className="flex-shrink-0">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg ${
                                comm.type === 'email' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                                comm.type === 'phone' ? 'bg-gradient-to-r from-green-500 to-green-600' :
                                'bg-gradient-to-r from-purple-500 to-purple-600'
                              }`}>
                                {comm.type === 'email' && <Mail className="text-white" size={18} />}
                                {comm.type === 'phone' && <Phone className="text-white" size={18} />}
                                {comm.type === 'whatsapp' && <MessageSquare className="text-white" size={18} />}
                              </div>
                            </div>

                            {/* Communication content */}
                            <div className="flex-1 min-w-0">
                              <div className={`p-6 rounded-2xl shadow-sm border transition-all duration-200 ${
                                comm.direction === 'inbound' ? 'bg-blue-50 border-blue-200 ml-8' : 'bg-gray-50 border-gray-200 mr-8'
                              }`}>
                                <div className="flex items-center justify-between mb-4">
                                  <div className="flex items-center space-x-3">
                                    <h5 className={`font-semibold ${
                                      comm.direction === 'inbound' ? 'text-blue-800' : 'text-gray-800'
                                    }`}>
                                      {comm.subject}
                                    </h5>
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      comm.direction === 'inbound' ? 'bg-blue-200 text-blue-700' : 'bg-gray-200 text-gray-700'
                                    }`}>
                                      {comm.direction === 'inbound' ? 'Reçu' : 'Envoyé'}
                                    </span>
                                    {comm.status && (
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                        comm.status === 'read' ? 'bg-green-200 text-green-700' :
                                        comm.status === 'sent' ? 'bg-yellow-200 text-yellow-700' :
                                        'bg-gray-200 text-gray-700'
                                      }`}>
                                        {comm.status === 'read' ? 'Lu' :
                                         comm.status === 'sent' ? 'Envoyé' :
                                         comm.status === 'completed' ? 'Terminé' : comm.status}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {new Date(comm.date).toLocaleDateString('fr-FR', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </div>
                                </div>

                                <p className={`text-sm mb-4 ${
                                  comm.direction === 'inbound' ? 'text-blue-700' : 'text-gray-700'
                                }`}>
                                  {comm.content}
                                </p>

                                {comm.duration && (
                                  <div className="flex items-center space-x-2 mb-3">
                                    <Clock className="text-gray-500" size={14} />
                                    <span className="text-sm text-gray-600">Durée: {comm.duration}</span>
                                  </div>
                                )}

                                {comm.attachments && comm.attachments.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {comm.attachments.map((attachment, idx) => (
                                      <div key={idx} className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg border border-gray-200 text-sm">
                                        <FileText className="text-gray-500" size={14} />
                                        <span className="text-gray-700">{attachment}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Timeline connector */}
                          {index < editingLead.communications.length - 1 && (
                            <div className="absolute left-6 top-16 w-0.5 h-6 bg-gray-300"></div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Actions rapides */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                    <h4 className="text-lg font-semibold text-white flex items-center">
                      <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                        <Zap className="text-white" size={20} />
                      </div>
                      Actions Rapides
                    </h4>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <button className="flex items-center justify-center space-x-3 p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-all duration-200">
                        <Mail className="text-blue-600" size={20} />
                        <span className="font-medium text-blue-700">Envoyer un email</span>
                      </button>
                      
                      <button className="flex items-center justify-center space-x-3 p-4 bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl transition-all duration-200">
                        <Phone className="text-green-600" size={20} />
                        <span className="font-medium text-green-700">Programmer un appel</span>
                      </button>
                      
                      <button className="flex items-center justify-center space-x-3 p-4 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-xl transition-all duration-200">
                        <MessageSquare className="text-orange-600" size={20} />
                        <span className="font-medium text-orange-700">Message WhatsApp</span>
                      </button>
                    </div>
                  </div>
                </div>

                {editMode.communications && (
                  <div className="flex justify-end space-x-4 bg-gray-50 px-6 py-4 rounded-xl border border-gray-200">
                    <button
                      onClick={() => handleSave('communications')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg transition-all duration-200"
                    >
                      <Save size={18} />
                      <span>Sauvegarder</span>
                    </button>
                    <button
                      onClick={() => handleCancel('communications')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 shadow-lg transition-all duration-200"
                    >
                      <X size={18} />
                      <span>Annuler</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">Notes & Historique</h3>
                    <p className="text-gray-600 mt-1">Notes internes et suivi de l'activité du lead</p>
                  </div>
                  {!editMode.notes && (
                    <button
                      onClick={() => handleToggleEdit('notes')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl hover:from-indigo-700 hover:to-indigo-800 shadow-lg transition-all duration-200"
                    >
                      <Plus size={18} />
                      <span>Ajouter une note</span>
                    </button>
                  )}
                </div>

                {/* Statistiques des notes */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <FileText className="text-white opacity-80" size={24} />
                      <span className="text-xs text-blue-100 font-medium">Total</span>
                    </div>
                    <div className="text-3xl font-bold mb-1">{editingLead.notes.length}</div>
                    <div className="text-blue-100 text-sm">Notes créées</div>
                  </div>

                  <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <MessageSquare className="text-white opacity-80" size={24} />
                      <span className="text-xs text-green-100 font-medium">Générales</span>
                    </div>
                    <div className="text-3xl font-bold mb-1">
                      {editingLead.notes.filter(n => n.type === 'general').length}
                    </div>
                    <div className="text-green-100 text-sm">Notes générales</div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <Phone className="text-white opacity-80" size={24} />
                      <span className="text-xs text-purple-100 font-medium">Appels</span>
                    </div>
                    <div className="text-3xl font-bold mb-1">
                      {editingLead.notes.filter(n => n.type === 'call').length}
                    </div>
                    <div className="text-purple-100 text-sm">Notes d'appel</div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <Users className="text-white opacity-80" size={24} />
                      <span className="text-xs text-orange-100 font-medium">Internes</span>
                    </div>
                    <div className="text-3xl font-bold mb-1">
                      {editingLead.notes.filter(n => n.type === 'internal').length}
                    </div>
                    <div className="text-orange-100 text-sm">Notes équipe</div>
                  </div>
                </div>

                {/* Timeline des notes */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
                    <h4 className="text-lg font-semibold text-white flex items-center">
                      <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                        <Clock className="text-white" size={20} />
                      </div>
                      Chronologie des Notes
                    </h4>
                  </div>
                  <div className="p-6">
                    <div className="space-y-6">
                      {editingLead.notes.map((note, index) => (
                        <div key={note.id} className="relative">
                          <div className="flex space-x-4">
                            {/* Timeline marker */}
                            <div className="flex-shrink-0">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg ${
                                note.type === 'general' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                                note.type === 'call' ? 'bg-gradient-to-r from-green-500 to-green-600' :
                                'bg-gradient-to-r from-orange-500 to-orange-600'
                              }`}>
                                {note.type === 'general' && <FileText className="text-white" size={18} />}
                                {note.type === 'call' && <Phone className="text-white" size={18} />}
                                {note.type === 'internal' && <Users className="text-white" size={18} />}
                              </div>
                            </div>

                            {/* Note content */}
                            <div className="flex-1 min-w-0">
                              <div className={`p-6 rounded-2xl shadow-sm border transition-all duration-200 ${
                                note.type === 'call' ? 'bg-green-50 border-green-200' :
                                note.type === 'internal' ? 'bg-orange-50 border-orange-200' :
                                'bg-blue-50 border-blue-200'
                              }`}>
                                <div className="flex items-center justify-between mb-4">
                                  <div className="flex items-center space-x-3">
                                    <div className="flex items-center space-x-2">
                                      <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                                        <User className="text-white" size={14} />
                                      </div>
                                      <span className={`font-semibold ${
                                        note.type === 'call' ? 'text-green-800' :
                                        note.type === 'internal' ? 'text-orange-800' :
                                        'text-blue-800'
                                      }`}>
                                        {note.author}
                                      </span>
                                    </div>
                                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                                      note.type === 'call' ? 'bg-green-200 text-green-700' :
                                      note.type === 'internal' ? 'bg-orange-200 text-orange-700' :
                                      'bg-blue-200 text-blue-700'
                                    }`}>
                                      {note.type === 'call' ? 'Appel' :
                                       note.type === 'internal' ? 'Interne' : 'Général'}
                                    </span>
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {new Date(note.date).toLocaleDateString('fr-FR', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </div>
                                </div>

                                <p className={`text-sm mb-4 leading-relaxed ${
                                  note.type === 'call' ? 'text-green-700' :
                                  note.type === 'internal' ? 'text-orange-700' :
                                  'text-blue-700'
                                }`}>
                                  {note.content}
                                </p>

                                {note.tags && note.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {note.tags.map((tag, tagIndex) => (
                                      <span key={tagIndex} className={`px-2 py-1 text-xs font-medium rounded-full ${
                                        note.type === 'call' ? 'bg-green-100 text-green-600' :
                                        note.type === 'internal' ? 'bg-orange-100 text-orange-600' :
                                        'bg-blue-100 text-blue-600'
                                      }`}>
                                        #{tag}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Timeline connector */}
                          {index < editingLead.notes.length - 1 && (
                            <div className="absolute left-6 top-16 w-0.5 h-6 bg-gray-300"></div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Ajouter une nouvelle note */}
                {editMode.notes && (
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                      <h4 className="text-lg font-semibold text-white flex items-center">
                        <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                          <Plus className="text-white" size={20} />
                        </div>
                        Nouvelle Note
                      </h4>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Type de note</label>
                            <select className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                              <option value="general">📝 Note générale</option>
                              <option value="call">📞 Note d'appel</option>
                              <option value="internal">👥 Note interne</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Tags (séparés par des virgules)</label>
                            <input
                              type="text"
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              placeholder="budget, preferences, contact..."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Contenu de la note</label>
                          <textarea
                            rows="4"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Saisissez votre note ici..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Documents liés */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-gray-500 to-gray-600 px-6 py-4">
                    <h4 className="text-lg font-semibold text-white flex items-center">
                      <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                        <Upload className="text-white" size={20} />
                      </div>
                      Documents Associés
                    </h4>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {editingLead.documents.map((doc, index) => (
                        <div key={doc.id} className="flex items-center space-x-3 p-4 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-all duration-200">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            doc.type === 'identity' ? 'bg-blue-100' :
                            doc.type === 'requirements' ? 'bg-green-100' :
                            'bg-purple-100'
                          }`}>
                            <FileText className={`${
                              doc.type === 'identity' ? 'text-blue-600' :
                              doc.type === 'requirements' ? 'text-green-600' :
                              'text-purple-600'
                            }`} size={18} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="font-medium text-gray-800 truncate">{doc.name}</h5>
                            <p className="text-xs text-gray-600">{doc.size} • {doc.uploadDate}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {editMode.notes && (
                  <div className="flex justify-end space-x-4 bg-gray-50 px-6 py-4 rounded-xl border border-gray-200">
                    <button
                      onClick={() => handleSave('notes')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg transition-all duration-200"
                    >
                      <Save size={18} />
                      <span>Sauvegarder</span>
                    </button>
                    <button
                      onClick={() => handleCancel('notes')}
                      className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 shadow-lg transition-all duration-200"
                    >
                      <X size={18} />
                      <span>Annuler</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetail;